import { HttpClient } from '@angular/common/http';
import { DOCUMENT } from '@angular/common';
import { Injectable, computed, inject, signal, type Signal, type WritableSignal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ClerkCustomerWebService } from './clerk-customer-web.service';
import { sanitizeCustomerReturnTo } from './clerk-customer-return-to';
import { type MeResponseBody } from './types/me-response-body.interface';

@Injectable({ providedIn: 'root' })
export class AuthSessionService {
  private readonly http: HttpClient = inject(HttpClient);
  private readonly clerk: ClerkCustomerWebService = inject(ClerkCustomerWebService);
  private readonly document: Document = inject(DOCUMENT);
  private readonly context: WritableSignal<MeResponseBody | null> = signal<MeResponseBody | null>(null);
  private readonly contextSubject: WritableSignal<string | null> = signal<string | null>(null);
  private pending: Promise<void> | null = null;
  private pendingSubject: string | null = null;
  private generation: number = 0;

  public readonly accessToken: Signal<null> = computed((): null => null);
  public readonly meContext: Signal<MeResponseBody | null> = computed((): MeResponseBody | null =>
    this.clerk.subject() !== null && this.clerk.subject() === this.contextSubject() ? this.context() : null,
  );
  public readonly isAuthenticated: Signal<boolean> = computed((): boolean => this.meContext() !== null);

  public async ensureSession(): Promise<void> {
    await this.clerk.initialize();
    const subject: string | null = this.clerk.subject();
    if (!subject) {
      this.clearSession();
      throw new Error('Customer session is missing.');
    }
    if (this.meContext()) {
      return;
    }
    if (this.pending && this.pendingSubject === subject) {
      return this.pending;
    }
    const generation: number = ++this.generation;
    this.pendingSubject = subject;
    const pending: Promise<void> = this.loadContext(subject, generation);
    this.pending = pending;
    try {
      await pending;
    } finally {
      if (this.pending === pending) {
        this.pending = null;
      }
    }
  }

  public async refreshMeContext(): Promise<MeResponseBody | null> {
    this.clearSession();
    await this.ensureSession();
    return this.meContext();
  }

  public getAccessToken(): null {
    return null;
  }

  public redirectToLogin(returnTo: string = this.document.location.pathname): void {
    this.redirect('/login', returnTo);
  }

  public redirectToSignup(returnTo: string = this.document.location.pathname): void {
    this.redirect('/signup', returnTo);
  }

  public async logout(): Promise<string | null> {
    this.clearSession();
    await this.clerk.signOut();
    return '/login?loggedOut=1';
  }

  private async loadContext(subject: string, generation: number): Promise<void> {
    const context: MeResponseBody = await firstValueFrom(this.http.get<MeResponseBody>('/api/v1/me'));
    if (generation !== this.generation || this.clerk.subject() !== subject) {
      throw new Error('Customer session changed.');
    }
    this.contextSubject.set(subject);
    this.context.set(context);
  }

  private redirect(path: string, returnTo: string): void {
    const target: string = sanitizeCustomerReturnTo(returnTo, this.document.location.origin);
    this.document.location.assign(`${path}?returnTo=${encodeURIComponent(target)}`);
  }

  private clearSession(): void {
    this.generation += 1;
    this.context.set(null);
    this.contextSubject.set(null);
    this.pending = null;
  }
}
